import { gql, useQuery } from '@apollo/client';

/**
 * "Can this go out without me?" (backlog 594cb5eb, app/Approvals Single Home):
 * the one answer Today, Account › Approvals and AI Agents read (apiserver
 * platform/services/agentControls.js). Approvals is the only place to change it,
 * and one pair of words says it everywhere.
 */

export const GOES_OUT_WITHOUT_YOU = gql`
  query GoesOutWithoutYou($accountId: ID!) {
    goesOutWithoutYou(accountId: $accountId) {
      items {
        kind
        id
        name
        actions
        summary
      }
      agents {
        id
        name
        publish
        surface
        delete
      }
      channels {
        id
        platform
        profileId
        name
        username
        mode
        publishing
      }
      rules {
        id
        name
        mode
        skips
        summary
      }
      social
      allRequireApproval
      snapshotId
      canChange
    }
  }
`;

export type GoesOutAction = 'publish' | 'surface' | 'delete';
export type ApprovalMode = 'ask' | 'out';

export interface GoesOutItem {
  kind: 'agent' | 'channel' | 'rule' | 'campaign';
  id: string;
  name: string;
  actions?: GoesOutAction[] | null;
  summary?: string | null;
}

export interface GoesOutAgent extends Record<GoesOutAction, 'allow' | 'ask' | 'never'> {
  id: string;
  name: string;
}

export interface GoesOutChannel {
  id: string;
  platform: string;
  profileId?: string | null;
  name?: string | null;
  username?: string | null;
  mode: ApprovalMode;
  publishing: boolean;
}

export interface GoesOutRule {
  id: string;
  name: string;
  mode: ApprovalMode;
  skips: boolean;
  summary: string;
}

export interface GoesOut {
  items: GoesOutItem[];
  agents: GoesOutAgent[];
  channels: GoesOutChannel[];
  rules: GoesOutRule[];
  social: boolean;
  allRequireApproval: boolean;
  snapshotId?: string | null;
  canChange: boolean;
}

export function useGoesOut(accountId?: string | null) {
  const { data, loading } = useQuery<{ goesOutWithoutYou: GoesOut }>(GOES_OUT_WITHOUT_YOU, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  return { goesOut: data?.goesOutWithoutYou || null, loading };
}

/** The word pair. */
export const ASK_WORDS = 'Ask me first';
export const OUT_WORDS = 'Goes out without asking';
export const modeWords = (mode: ApprovalMode) => (mode === 'out' ? OUT_WORDS : ASK_WORDS);

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** What counts: a campaign was approved once, so it's listed but isn't "without asking". */
const counted = (items: GoesOutItem[]) => items.filter((i) => i.kind !== 'campaign');

/** The one line Today, AI Agents and Approvals share. */
export function goesOutSentence(items: GoesOutItem[]): string {
  const n = counted(items).length;
  if (!n) return 'Everything asks you first.';
  return `${n === 1 ? '1 thing goes' : `${n} things go`} out without asking.`;
}

/** "1 agent, 2 channels and 1 rule." Approvals' summary only. */
export function goesOutList(items: GoesOutItem[]): string {
  const count = (kind: GoesOutItem['kind']) => items.filter((i) => i.kind === kind).length;
  const parts = [
    count('agent') ? plural(count('agent'), 'agent') : '',
    count('channel') ? plural(count('channel'), 'channel') : '',
    count('rule') ? plural(count('rule'), 'rule') : '',
  ].filter(Boolean);
  if (!parts.length) return '';
  const list =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return list.charAt(0).toUpperCase() + list.slice(1);
}

const SKIP_WORDS: Array<[GoesOutAction, string]> = [
  ['publish', 'publishing'],
  ['surface', 'site changes'],
  ['delete', 'deleting'],
];

/** The line on an agent's card: what goes out without asking, or null when nothing does. */
export function agentSkipLine(rules?: Partial<Record<GoesOutAction, string>> | null) {
  const words = SKIP_WORDS.filter(([key]) => rules?.[key] === 'allow').map(([, word]) => word);
  if (!words.length) return null;
  const list =
    words.length === 1
      ? words[0]
      : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  return `${OUT_WORDS}: ${list}`;
}

export const APPROVALS_PATH = '/settings/approvals';
export const AGENTS_PATH = '/settings/ai-agents';
export const PERM_ACTIVITY_PATH = '/settings/activity?filter=perm';

/** Where a row sits on Approvals: a chip elsewhere opens the page there. */
export const channelAnchor = (id: string) => `appr-channel-${id}`;
export const ruleAnchor = (id: string) => `appr-rule-${id}`;
export const agentAnchor = (id: string, key: GoesOutAction) => `appr-agent-${id}-${key}`;
export const approvalsAt = (anchor: string) => `${APPROVALS_PATH}#${anchor}`;
